import React, { useState, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import WorkflowOverview from '../components/WorkflowOverview';
import ReportWorkflowOverview from '../components/ReportWorkflowOverview';
import ApiEndpoint from '../components/ApiEndpoint';
import DataModelCard from '../components/DataModel';
import { apiEndpoints } from '../data/apiEndpoints';
import { dataModels } from '../data/dataModels';

type DemoFilter = 'all' | 'api-demo' | 'report-demo';

const MainPage: React.FC = () => {
  const { authToken } = useAuth();
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [highlightedEndpoint, setHighlightedEndpoint] = useState<string | null>(null);
  const [filter, setFilter] = useState<DemoFilter>('all');
  const endpointRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const filteredEndpoints = useMemo(() => {
    if (filter === 'all') return apiEndpoints;
    return apiEndpoints.filter(ep => (ep.usedBy ?? []).includes(filter));
  }, [filter]);

  const scrollToFirstMatch = (predicate: (step: number) => boolean, step: number) => {
    setSelectedStep(step);
    const endpoint = apiEndpoints.find(ep => predicate(step));

    if (endpoint) {
      // Clicking a workflow step implicitly shows every endpoint again, so the
      // target endpoint is guaranteed to be present regardless of the current filter.
      setFilter('all');
      setTimeout(() => {
        const element = document.getElementById(`endpoint-${endpoint.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightedEndpoint(endpoint.id);
          setTimeout(() => setHighlightedEndpoint(null), 2000);
        }
      }, 0);
    }
  };

  const handleStepClick = (step: number) => {
    scrollToFirstMatch(s => apiEndpoints.some(ep => ep.step === s), step);
  };

  const handleReportStepClick = (step: number) => {
    scrollToFirstMatch(s => apiEndpoints.some(ep => ep.reportDemoStep === s), step);
  };

  return (
    <Layout>
      <div className="container">
        {/* Workflow Overviews */}
        <div id="workflow">
          <WorkflowOverview
            onStepClick={handleStepClick}
            selectedStep={selectedStep}
          />
          <ReportWorkflowOverview
            onStepClick={handleReportStepClick}
            selectedStep={selectedStep}
          />
        </div>

        {/* Data Models */}
        <section id="data-models" className="endpoints-section">
          <h2>Data Models</h2>
          <p className="section-subtitle">
            Shared object shapes referenced across the endpoints below — the Project → Layout → Configuration → Job
            hierarchy, per-file-type validation payloads, the dual pile-binning analysis format, map layers, and the
            report-generation request body.
          </p>
          <div className="endpoints-list">
            {dataModels.map(model => (
              <DataModelCard key={model.id} model={model} />
            ))}
          </div>
        </section>

        {/* API Endpoints */}
        <section id="endpoints" className="endpoints-section">
          <h2>API Endpoints</h2>
          <p className="section-subtitle">
            Every endpoint documented here is either called by API Demo, called by Report Demo, or implemented but not
            yet wired into either demo (clearly labeled). Filter to see just what one page uses.
          </p>
          <div className="demo-filter-bar">
            <button
              className={`demo-filter-button ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Endpoints ({apiEndpoints.length})
            </button>
            <button
              className={`demo-filter-button api-demo ${filter === 'api-demo' ? 'active' : ''}`}
              onClick={() => setFilter('api-demo')}
            >
              Used by API Demo ({apiEndpoints.filter(ep => (ep.usedBy ?? []).includes('api-demo')).length})
            </button>
            <button
              className={`demo-filter-button report-demo ${filter === 'report-demo' ? 'active' : ''}`}
              onClick={() => setFilter('report-demo')}
            >
              Used by Report Demo ({apiEndpoints.filter(ep => (ep.usedBy ?? []).includes('report-demo')).length})
            </button>
          </div>
          <div className="endpoints-list">
            {filteredEndpoints.map((endpoint) => (
              <div
                key={endpoint.id}
                id={`endpoint-${endpoint.id}`}
                ref={(el) => { endpointRefs.current[endpoint.id] = el; }}
              >
                <ApiEndpoint
                  endpoint={endpoint}
                  authToken={authToken}
                  isHighlighted={highlightedEndpoint === endpoint.id}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default MainPage;
